import './CustomTips.sass';

import WebApp from '@twa-dev/sdk';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRoute } from 'react-router5';

import { Api } from '$api';
import { useUserInfo } from '$hooks';
import { Loader, UnitName, UnitPhoto, Verified } from '$shared/components';
import { mainSelector } from '$store/main';
import { PaymentMethodOption } from '$store/pay/interface';
import { profileActions, profileSelector } from '$store/profile';
import { toastActions } from '$store/toast';
import { Button } from '$uikit';

export const CustomTips = () => {
  const dispatch = useDispatch();
  const { route } = useRoute();
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const { authorId, amount, message, method } = route.params;

  const parsedAuthorId = Number.parseInt(authorId, 10);
  const parsedAmount = Number.parseFloat(amount);

  const user = useUserInfo(parsedAuthorId);
  const { isLoading: isUserLoading } = useSelector(profileSelector);
  const { countryCode } = useSelector(mainSelector);

  useEffect(() => {
    if (Number.isNaN(parsedAuthorId)) {
      dispatch(toastActions.setToastFail('Author ID must be a valid number'));
      return;
    }

    dispatch(profileActions.loadProfile({ ownerId: parsedAuthorId }));
  }, [parsedAuthorId, dispatch]);

  if (Number.isNaN(parsedAmount) || parsedAmount < 10 || parsedAmount > 500) {
    dispatch(
      toastActions.setToastFail('Amount must be a valid number between 10 and 500'),
    );
    return null;
  }

  if (!message) {
    dispatch(toastActions.setToastFail('Message is required'));
    return null;
  }

  if (message.length > 128) {
    dispatch(toastActions.setToastFail('Message must be less than 128 characters'));
    return null;
  }

  if (!method || !['sbp', 'world'].includes(method)) {
    dispatch(toastActions.setToastFail('Method must be either sbp or world'));
    return null;
  }

  const handlePaymentClick = async () => {
    setIsPaymentLoading(true);
    dispatch(toastActions.setToastLoading());

    try {
      const tipsResponse = await Api.post<any, any>(`/im/${authorId}/tips`, {
        amount: parsedAmount,
        currency: 'eur',
        message: message,
      });

      if (!tipsResponse.tips?.id) {
        throw new Error('Failed to create tips record');
      }

      const payResponse = await Api.post<any, any>('/pay/top_up', {
        amount: parsedAmount,
        countryCode,
        triggerPay: 'tips',
        triggerPayId: tipsResponse.tips.id,
        paymentMethod:
          method === 'sbp' ? PaymentMethodOption.SBP : PaymentMethodOption.CARD,
      });

      if (payResponse.url) {
        dispatch(toastActions.hideToast());

        if (window.isWebApp) {
          WebApp.openLink(payResponse.url);
          WebApp.close();
        } else {
          window.location.href = payResponse.url;
          window.close();
        }
      } else {
        throw new Error('No payment URL received');
      }
    } catch (error) {
      dispatch(toastActions.setToastFail(error.message || 'Payment failed'));
    } finally {
      setIsPaymentLoading(false);
    }
  };

  if (isUserLoading || !user) {
    return (
      <div className="CustomTips">
        <Loader isWrapped />
      </div>
    );
  }

  return (
    <div className="CustomTips">
      <div className="CustomTips__header">
        <UnitPhoto unit={user} size="large" round />
        <div className="CustomTips__header__info">
          <div className="CustomTips__header__name_wrap">
            <span className="CustomTips__header__name">
              <UnitName unit={user} />
            </span>
            <Verified objectId={user.id} large />
          </div>
        </div>
      </div>

      <div className="CustomTips__content">
        <Button onClick={handlePaymentClick} disabled={isPaymentLoading}>
          Оплатить €{parsedAmount}
        </Button>
      </div>
    </div>
  );
};
